import { ingredientToOption } from "@/lib/add-ingredient-utilities";
import {
  ShapedClass,
  dot,
  searchClasses,
  shapeClass,
  shapeClassLinear,
} from "@/lib/class-utilities";
import {
  combineUnits,
  getBaseUnit,
  getUnitOptions,
  getUnitOrFallback,
  stringIsValidNumber,
  stringToNumber,
  toAbsoluteAmount,
  toRelativeAmount,
  toRelativePrice,
  unitToOption,
} from "@/lib/measurement";
import { useRouter } from "@/lib/next-nprogress";
import { isRecipeLocked } from "@/lib/recipe-utilities";
import {
  TagOptionType,
  classNames,
  ellipsise,
  isAdmin,
  isAssistant,
  isStudent,
  isTeacher,
  searchTags,
  sortByFn,
} from "@/lib/util";
import {
  AncillariesComponent,
  Exact,
  IngredientQuery,
  IngredientsQuery,
  OrderCarouselQuery,
  PriceWarning_Range,
  PriceWarning_Type,
  RecipeQuery,
  RecipeSectionCreateWithoutRecipeInput,
  RecipeUpdateInput,
  RecipeWhereUniqueInput,
  Recipe_MadeInGroups,
  UpdateRecipeMutation,
  useCreateManyRecipesMutation,
  useDeleteRecipeMutation,
  useUpdateRecipeMutation,
} from "@/queries/autogenerate/graphql";
import {
  Field,
  Label,
  Popover,
  PopoverButton,
  PopoverPanel,
  Switch,
  Transition,
} from "@headlessui/react";
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  BanknotesIcon,
  CameraIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CpuChipIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  LinkIcon,
  LockClosedIcon,
  LockOpenIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PencilIcon,
  PlusCircleIcon,
  PlusIcon,
  PrinterIcon,
  ShoppingCartIcon,
  TrashIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import cuid from "cuid";
import { format, formatDistanceToNow } from "date-fns";
import Link from "next/link";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import gfm from "remark-gfm";
import { UseMutationExecute } from "urql";
import { AsyncSelectIngredient } from "../AsyncSelectIngredient";
import { InformationCircleComponent } from "../Commons";
import CreateIngredients from "../CreateIngredients";
import DialogComponent from "../Dialog";
import DragNDrop, { SortableColumn } from "../DragNDrop";
import ErrorMessage from "../ErrorMessage";
import GetImageURL from "../GetImageURL";
import IngredientsGridItem from "../IngredientsGridItem";
import { CurrentUser, OrderDialogContext, UserContext } from "../Page";
import GetLatestOrderSessions from "../QueryHelpers/GetLatestOrderSessions";
import GetRecipe from "../QueryHelpers/GetRecipe";
import TextEditor from "../TextEditor";
import { Input, InventoryGrid, Notice } from "../styles/Layout";
import { TextContentWrapper } from "../styles/TextContentWrapper";
import Tooltip from "../styles/Tooltip";
import DragIndicatorIcon from "../svg/DragIndicatorIcon";
import ExploreIcon from "../svg/ExploreIcon";
import YoutubeSearchedForIcon from "../svg/YoutubeSearchedForIcon";
import RecipesGridIngredient from "./RecipesGridIngredient";
import { MoveRecipesToFolderDialog } from "./RecipesPage";
import {
  getPathToFolder,
  getRecipeIngredients,
  getRecipeSections,
} from "./util";

interface DeleteRecipeProps {
  recipe: OrderCarouselQuery["recipes"][number];
  user: CurrentUser;
  setDeleted: Dispatch<SetStateAction<boolean>>;
  setSelectedRecipes?: Dispatch<SetStateAction<string[]>>;
}

const DeleteRecipe: React.FC<DeleteRecipeProps> = ({
  recipe,
  user,
  setDeleted,
  setSelectedRecipes,
}) => {
  const locked = isRecipeLocked(recipe, user);

  const [{ fetching, error }, deleteRecipe] = useDeleteRecipeMutation();

  if (error) return <ErrorMessage error={error} />;

  return locked ? null : (
    <UserContext.Consumer>
      {(user) => (
        <Tooltip content="Delete This Recipe">
          <button
            disabled={
              (isStudent(user) && !user.school?.permDeleteRecipes) || fetching
            }
            onClick={async () => {
              await deleteRecipe({ id: recipe.id });

              setDeleted(true);

              if (setSelectedRecipes) {
                setSelectedRecipes((prevSelected) =>
                  prevSelected.filter((id) => id !== recipe.id)
                );
              }
            }}
          >
            {fetching ? (
              <div className="spinner m-1.5 h-6 w-6" />
            ) : (
              <TrashIcon className="m-1.5 w-6 text-orange-600 hover:text-orange-700" />
            )}
          </button>
        </Tooltip>
      )}
    </UserContext.Consumer>
  );
};

interface InfoItemProps {
  information: NonNullable<RecipeQuery["recipe"]>["recipeInformation"][number];
  recipeID: string;
  fetching: boolean;
  updateRecipe: UseMutationExecute<
    UpdateRecipeMutation,
    Exact<{
      where: RecipeWhereUniqueInput;
      data: RecipeUpdateInput;
    }>
  >;
}

const InfoItem: React.FC<InfoItemProps> = ({
  information: { id, contents },
  recipeID,
  fetching,
  updateRecipe,
}) => {
  const [updating, setUpdating] = useState(false);
  const [newContents, setContents] = useState(contents);

  return (
    <li className="markdown flex items-center">
      {updating ? (
        <form
          className="my-5 flex items-center justify-between"
          style={{ width: "400px" }}
          onSubmit={(e) => {
            e.preventDefault();

            updateRecipe({
              where: { id: recipeID },
              data: {
                recipeInformation: {
                  update: [
                    {
                      where: { id },
                      data: { contents: { set: newContents } },
                    },
                  ],
                },
              },
            });
            setUpdating(false);
          }}
        >
          <Input
            value={newContents}
            style={{ width: "350px" }}
            autoFocus
            onChange={(e) => setContents(e.target.value)}
            required
          />
          <button
            type="submit"
            className="my-0 px-1.5 py-0.5"
            disabled={fetching}
          >
            <CheckCircleIcon className="w-6 text-green-600 transition-colors hover:text-green-800" />
          </button>
          <button
            className="my-0 px-1.5 py-0.5"
            onClick={() => setUpdating(false)}
          >
            <XCircleIcon className="w-6 text-gray-700 transition-colors hover:text-gray-900" />
          </button>
        </form>
      ) : (
        <>
          <ReactMarkdown plugins={[gfm]} linkTarget="_blank">
            {contents}
          </ReactMarkdown>
          <Tooltip content="Edit">
            <button
              className="my-0 px-1.5 py-0.5"
              onClick={() => setUpdating(true)}
            >
              <PencilIcon className="w-3 text-gray-700 hover:text-gray-900" />
            </button>
          </Tooltip>
          <Tooltip content="Remove Information">
            <button
              className="my-0 px-1.5 py-0.5"
              disabled={fetching}
              onClick={() => {
                updateRecipe({
                  where: { id: recipeID },
                  data: {
                    recipeInformation: {
                      delete: [{ id }],
                    },
                  },
                });
              }}
            >
              <TrashIcon className="w-3 text-orange-500 hover:text-orange-700" />
            </button>
          </Tooltip>
        </>
      )}
    </li>
  );
};

interface RecipeSectionComponentProps {
  user: CurrentUser;
  index: number;
  recipe: NonNullable<RecipeQuery["recipe"]>;
  section: NonNullable<RecipeQuery["recipe"]>["recipeSections"][number] & {
    recipeIngredients: NonNullable<RecipeQuery["recipe"]>["recipeIngredients"];
  };
  updateRecipe: UseMutationExecute<
    UpdateRecipeMutation,
    Exact<{
      where: RecipeWhereUniqueInput;
      data: RecipeUpdateInput;
    }>
  >;
  fetching: boolean;
}

export const RecipeSectionComponent: React.FC<RecipeSectionComponentProps> = ({
  user,
  index,
  recipe,
  section,
  updateRecipe,
  fetching,
}) => {
  const [isEditingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(section.name);

  const cost = section.recipeIngredients.reduce(
    (acc, cur) =>
      acc +
      toRelativePrice(
        cur.ingredient.prices[0],
        { amount: recipe.servings * cur.amount },
        cur.ingredient
      ),
    0
  );

  const locked = isRecipeLocked(recipe, user);

  return (
    <SortableColumn key={section.id} id={section.id} index={index}>
      {({ ref, handleRef, isDropTarget }) => (
        <div ref={ref}>
          <div className="rounded border border-solid border-gray-200 p-2 transition-colors hover:border-gray-400">
            {section.id != "sectionLess" ? (
              <div className="group flex max-w-xl items-center gap-2.5 py-1.5">
                {isEditingName ? (
                  <form
                    className="flex items-center"
                    onSubmit={async (e) => {
                      e.preventDefault();

                      if (newName.trim().length > 0) {
                        if (newName != section.name) {
                          await updateRecipe({
                            where: { id: recipe.id },
                            data: {
                              recipeSections: {
                                update: [
                                  {
                                    where: { id: section.id },
                                    data: { name: { set: newName } },
                                  },
                                ],
                              },
                            },
                          });
                        }

                        setEditingName(false);
                      } else {
                        toast.error("Section name cannot be empty");
                      }
                    }}
                  >
                    <input
                      type="text"
                      autoFocus
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="rounded border border-solid border-gray-300 p-1.5"
                      disabled={fetching}
                    />
                    {newName ? (
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={fetching}
                      >
                        <CheckCircleIcon className="mr-0.5 w-5 text-green-700" />
                        Confirm
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        setEditingName(false);
                      }}
                      disabled={fetching}
                    >
                      <XCircleIcon className="mr-0.5 w-5 text-gray-700" />
                      Cancel
                    </button>
                  </form>
                ) : (
                  <>
                    {locked ? null : (
                      <div
                        className="flex items-center justify-center"
                        title="Sort Sections"
                        ref={handleRef}
                      >
                        <DragIndicatorIcon className="w-4" />
                      </div>
                    )}
                    <h4 className="font-semibold text-gray-800">
                      {section.name || "Unnamed Section"}
                      {cost > 0 ? (
                        <span className="ml-2 font-normal text-amber-700 opacity-50 transition group-hover:opacity-100">
                          ${cost.toFixed(2)}
                        </span>
                      ) : null}
                    </h4>
                    <Tooltip content="Edit Section Name">
                      <button onClick={() => setEditingName(true)} className="">
                        <PencilIcon className="w-0 text-gray-500 opacity-0 transition hover:text-gray-700 group-hover:w-5 group-hover:opacity-100" />
                      </button>
                    </Tooltip>
                  </>
                )}
                <hr className="flex-1 border-gray-300" />
                {isEditingName ? null : (
                  <Tooltip content="Remove Section">
                    <button
                      onClick={() => {
                        updateRecipe({
                          where: { id: recipe.id },
                          data: {
                            recipeSections: {
                              delete: [{ id: section.id }],
                            },
                          },
                        });
                      }}
                      className=""
                      disabled={fetching}
                    >
                      <TrashIcon className="w-5 text-orange-500 opacity-0 transition hover:text-orange-700 group-hover:opacity-100" />
                    </button>
                  </Tooltip>
                )}
              </div>
            ) : (
              <div className="group flex max-w-xl items-center gap-1.5 py-1.5">
                {locked ? null : (
                  <div
                    className="flex items-center justify-center"
                    title="Sort Sections"
                    ref={handleRef}
                  >
                    <DragIndicatorIcon className="w-4" />
                  </div>
                )}
                <h4 className="font-semibold text-gray-800/30">
                  Default Section
                  {cost > 0 ? (
                    <span className="ml-2 font-normal text-amber-700 opacity-50 transition group-hover:opacity-100">
                      ${cost.toFixed(2)}
                    </span>
                  ) : null}
                </h4>
              </div>
            )}
            <ul>
              {section.recipeIngredients.length ? (
                section.recipeIngredients.map((ingredient, index) => (
                  <RecipesGridIngredient
                    key={ingredient.id}
                    recipe={recipe}
                    ingredient={ingredient}
                    index={index}
                    updateRecipe={updateRecipe}
                    user={user}
                  />
                ))
              ) : section.id == "sectionLess" || isDropTarget ? null : (
                <div className="rounded border border-dashed border-gray-300 bg-gradient-to-tr from-cyan-50 to-gray-50 p-1.5 text-center">
                  <h3>Empty Section</h3>
                  <small>
                    Add ingredients to this section by dragging them.
                  </small>
                </div>
              )}
            </ul>
          </div>
        </div>
      )}
    </SortableColumn>
  );
};

interface ExpandedItemProps {
  user: CurrentUser;
  recipe: NonNullable<RecipeQuery["recipe"]>;
  isFragment?: boolean;
  setExpand: Dispatch<SetStateAction<boolean>>;
  setDeleted: Dispatch<SetStateAction<boolean>>;
  setArchived: Dispatch<SetStateAction<boolean>>;
  isNative?: boolean;
  selectedRecipes?: string[];
  setSelectedRecipes?: Dispatch<SetStateAction<string[]>>;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  folder:
    | NonNullable<CurrentUser["school"]>["recipeCollections"][0]
    | null
    | undefined;
}

const ExpandedItem: React.FC<ExpandedItemProps> = ({
  user,
  recipe,
  isFragment,
  setExpand,
  setDeleted,
  setArchived,
  isNative,
  selectedRecipes,
  setSelectedRecipes,
  sortedFolders,
  folder,
}) => {
  // const [isChangingName, setNameChange] = useState(false);
  const [newName, setNewName] = useState(recipe.name);
  const [firstNameUpdate, setFirstNameUpdate] = useState(true);
  // Auto-save using a debounced function
  useEffect(() => {
    if (firstNameUpdate) {
      console.log("crisis averted");
      setFirstNameUpdate(false);
      return;
    }

    const handler = setTimeout(async () => {
      console.log("auto-update");

      await updateRecipe({
        where: { id: recipe.id },
        data: { name: { set: newName } },
      });
      onUpdateRecipeCompleted();
    }, 500);

    // Return a cleanup function that will be called every ...
    // ... time useEffect is re-called.
    // If it is re-called before the timeout, the timeout will be cleared ...
    // ... thus debouncing.
    return () => {
      clearTimeout(handler);
    };
  }, [newName]);

  const [isChangingServings, setServingsChange] = useState(false);
  const [servings, setServings] = useState(recipe.servings);

  const [madeInGroupsValue, setMadeInGroupsValue] =
    useState<Recipe_MadeInGroups | null>(recipe.madeInGroups || null);

  const [isAddingSection, setAddingSection] = useState(false);
  const [sectionName, setSectionName] = useState("");

  const [isChangingMethod, setMethodChange] = useState(false);
  // const [method, setMethod] = useState(recipe.method || "");

  const [isAddingInfo, setAddingInfo] = useState(false);
  const [info, setInfo] = useState("");

  const [isAddingClass, setAddClass] = useState(false);
  const [newClass, setNewClass] = useState<ShapedClass | null>(null);

  const [isAddingTag, setAddTag] = useState(false);
  const [newTags, setNewTags] = useState<readonly TagOptionType[]>([]);

  const [isAddingIngredient, setAddingIngredient] = useState(false);
  const [newIngredient, setNewIngredient] = useState<
    IngredientsQuery["ingredients"][number] | null
  >(null);

  // const newIngredient = ingredients.find((i) => i.id == newIngredientID);

  const [newIngredientAmount, setNewIngredientAmount] = useState<
    number | string
  >(1);
  const [newIngredientUnit, setNewIngredientUnit] = useState<
    IngredientsQuery["ingredients"][number]["units"][number] | null
  >(null);
  const [newIngredientPreparation, setNewIngredientPreparation] = useState("");

  const [editDialog, setEditDialog] = useState(false);
  const open = () => setEditDialog(true);
  const close = () => setEditDialog(false);

  const [reorderPulse, setReorderPulse] = useState(false);

  const [isCreatingIngredients, setCreatingIngredients] = useState(false);

  const [{ fetching, error }, updateRecipe] = useUpdateRecipeMutation();

  // Check if the unit still exists on the ingredient
  const unitExists = newIngredient
    ? combineUnits(newIngredient).find((cu) => cu.id === newIngredientUnit?.id)
    : null;

  const recipeSections = getRecipeSections(recipe);
  const UUIDfy = useMemo(
    () => (recipeSections: ReturnType<typeof getRecipeSections>) =>
      recipeSections.map((rs) => ({
        ...rs,
        uuid: rs.id,
        recipeIngredients: rs.recipeIngredients.map((ri) => ({
          ...ri,
          uuid: ri.id,
        })),
      })),
    []
  );

  const onUpdateRecipeCompleted = () => {
    setAddingSection(false);
    setSectionName("");

    setServingsChange(false);
    setMethodChange(false);

    setAddingInfo(false);
    setInfo("");

    setAddClass(false);
    setNewClass(null);

    setAddTag(false);
    setNewTags([]);
  };

  const addIngredientButton = useRef<HTMLButtonElement>(null);
  const addIngredientInput = useRef<HTMLInputElement>(null);
  const newIngredientAmountInput = useRef<HTMLInputElement>(null);

  const cost =
    user.school?.plan !== "basic"
      ? +recipe.recipeIngredients
          .reduce(
            (acc, cur) =>
              acc +
              toRelativePrice(
                cur.ingredient.prices[0],
                { amount: recipe.servings * cur.amount },
                cur.ingredient
              ),
            0
          )
          .toFixed(2)
      : 0;

  const costedIngredientCount = recipe.recipeIngredients.filter(
    (cur) => cur.ingredient.prices[0]
  ).length;
  const costedIngredientPercentage = Math.round(
    (costedIngredientCount / recipe.recipeIngredients.length) * 100
  );

  const priceLimit = sortByFn(
    user.school?.priceWarnings.filter(
      (pw) => pw.type === PriceWarning_Type.Recipe
    ) || [],
    (pw) =>
      pw.range === PriceWarning_Range.Recipe
        ? pw.price / 100 / recipe.servings
        : pw.price / 100 / pw.amount
  )[0];
  const unitaryPriceLimit = priceLimit
    ? priceLimit.range === PriceWarning_Range.Recipe
      ? priceLimit.price / 100 / recipe.servings
      : priceLimit.price / 100 / priceLimit.amount
    : 0;

  const abovePriceLimit =
    cost && priceLimit && cost / recipe.servings > unitaryPriceLimit;

  const locked = isRecipeLocked(recipe, user);
  const canCreateIngredients = !(
    isStudent(user) && !user.school?.permCreateIngredients
  );

  const unsortedClasses =
    isAdmin(user) || isAssistant(user)
      ? user.school?.classes
      : user.school?.classes.filter((c) =>
          c.users.some((u) => u.id === user.id)
        );

  return (
    <div
      id={`${recipe.id}${isFragment ? "" : "-default"}`}
      className={classNames(
        "group relative bg-white transition-all",
        isFragment ? "border-4 border-green-400" : "",
        abovePriceLimit ? "border border-solid border-orange-500" : "",
        selectedRecipes?.includes(recipe.id)
          ? "ring ring-blue-400 ring-offset-2"
          : ""
      )}
    >
      <ErrorMessage error={error} />

      <div
        className={
          recipe.image && user.school?.fullRecipePosition == "top"
            ? "bg-cover bg-center pt-56"
            : ""
        }
        style={
          recipe.image && user.school?.fullRecipePosition == "top"
            ? {
                backgroundImage: `linear-gradient(to bottom, transparent, transparent 50%, white), url("${recipe.image}")`,
                margin: "-10px 0 0 -10px",
                width: "calc(100% + 10px + 10px)",
                borderRadius: "6px",
              }
            : {}
        }
      >
        <div
          className={`${
            recipe.image && user.school?.fullRecipePosition == "top"
              ? "mx-4 my-1.5"
              : ""
          } flex w-full items-center justify-between`}
        >
          {selectedRecipes && setSelectedRecipes ? (
            <Tooltip content="Select recipe for bulk actions.">
              <input
                type="checkbox"
                id={`select-recipe-${recipe.id}`}
                name={`select-recipe-${recipe.id}`}
                checked={selectedRecipes.includes(recipe.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRecipes([...selectedRecipes, recipe.id]);
                  } else {
                    setSelectedRecipes(
                      selectedRecipes.filter((id) => id !== recipe.id)
                    );
                  }
                }}
                className="absolute -left-1 -top-1 z-10 size-6 rounded-full border-gray-300 text-blue-600 opacity-0 transition focus:ring-blue-600 group-hover:opacity-100"
              />
            </Tooltip>
          ) : null}
          <Link
            prefetch={false}
            href={`/event-log?id=${recipe.id}`}
            className="absolute -right-3 -top-3 z-10 rounded-full bg-white p-0.5 opacity-0 shadow-md transition delay-150 group-hover:opacity-100 print:hidden"
          >
            <Tooltip content="See Recipe Version History" placement="right">
              <span>
                <YoutubeSearchedForIcon className="icon my-1.5" />
              </span>
            </Tooltip>
          </Link>

          <MainRecipeActions
            user={user}
            recipe={recipe}
            setDeleted={setDeleted}
            setArchived={setArchived}
            sortedFolders={sortedFolders}
            folder={folder}
            locked={locked}
            recipeSections={recipeSections}
            isNative={isNative}
            setSelectedRecipes={setSelectedRecipes}
          />
        </div>
      </div>

      {/* Recipe Name */}
      <div className="m-1.5 mb-0 flex items-center justify-between gap-2.5">
        <input
          type="text"
          value={newName}
          placeholder={"Recipe Name"}
          className="flex-1 rounded border border-solid border-transparent p-1.5 text-2xl font-medium transition hover:border-gray-200 focus:border-gray-200"
          onChange={(e) => setNewName(e.target.value)}
          disabled={locked}
        />
        {fetching ? <div className="spinner" /> : null}
      </div>

      {/* Created / Updated / Last Ordered */}
      <div>
        <small className="mb-2 block font-normal">
          Created: {format(new Date(recipe.createdAt), "p EEEE LLLL do, yyyy")}
        </small>
        <small className="mb-2 block">
          Updated: {format(new Date(recipe.updatedAt), "p EEEE LLLL do, yyyy")}
        </small>
        <GetLatestOrderSessions recipeId={recipe.id}>
          {(orderSessions) =>
            orderSessions.length ? (
              <>
                {orderSessions.slice(0, 1).map((orderSession) => (
                  <Tooltip
                    content={format(
                      new Date(orderSession.createdAt),
                      "p EEEE LLLL do, yyyy"
                    )}
                    key={orderSession.id}
                  >
                    <small className="inline-block rounded border border-solid border-cyan-200 bg-cyan-50 px-1.5 py-0.5">
                      Last ordered{" "}
                      {formatDistanceToNow(new Date(orderSession.createdAt), {
                        addSuffix: true,
                      })}
                    </small>
                  </Tooltip>
                ))}
              </>
            ) : null
          }
        </GetLatestOrderSessions>
      </div>

      {/* Creation Process */}
      {recipe.process ? (
        <div className="m-1.5">
          {recipe.process == "manual" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg  bg-slate-100 p-1.5 font-semibold">
              <PencilIcon className="mx-1.5 inline-block w-5 text-slate-500" />
              <span className="text-slate-900">Created Manually</span>
            </div>
          ) : recipe.process == "duplicated" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg  bg-slate-100 p-1.5 font-semibold">
              <DocumentDuplicateIcon className="mx-1.5 inline-block w-5 text-slate-500" />
              <span className="text-slate-900">Duplicated</span>
            </div>
          ) : recipe.process == "document" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg bg-emerald-50 p-1.5 text-base font-semibold">
              <DocumentTextIcon className="mx-1.5 inline-block w-5 text-emerald-500" />
              <span className="text-emerald-900">Uploaded from Document</span>
            </div>
          ) : recipe.process == "discovered" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg bg-gradient-to-tr from-cyan-100 to-green-100 p-1.5 text-base font-semibold">
              <MapPinIcon className="mx-1.5 inline-block w-5 text-cyan-500" />
              <span className="text-cyan-900">Discovered on ClassCart</span>
            </div>
          ) : recipe.process == "website" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg bg-sky-50 p-1.5 text-base font-semibold">
              <LinkIcon className="mx-1.5 inline-block w-5 text-sky-500" />
              <span className="text-sky-900">Uploaded from Website</span>
            </div>
          ) : recipe.process == "ai generated" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg bg-cyan-50 p-1.5 text-base font-semibold">
              <CpuChipIcon className="mx-1.5 inline-block w-5 text-cyan-500" />
              <span className="text-cyan-900">AI Generated</span>
            </div>
          ) : recipe.process == "searched website" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg bg-amber-50 p-1.5 text-base font-semibold">
              <MagnifyingGlassIcon className="mx-1.5 inline-block w-5 text-amber-500" />
              <span className="text-amber-900">Searched Recipe Website</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Source */}
      {recipe.source ? (
        <div className="my-6">
          <small>
            Source:{" "}
            <Tooltip content={recipe.source}>
              <a
                href={recipe.source}
                target="_blank"
                rel="noreferrer"
                className="text-blue-500 hover:text-blue-700"
              >
                {ellipsise(
                  recipe.source.replace(/^(https?:\/\/)?(www\.?)?/, ""),
                  user.school?.fullRecipePosition == "right" ? 25 : 50
                )}
              </a>
            </Tooltip>
          </small>
        </div>
      ) : null}

      {/* Connected Classes */}
      <div style={{ margin: "5px" }}>
        {!isAddingClass &&
          (recipe.classes.length ? (
            recipe.classes.map((t) => (
              <span
                className="tag tag-cyan group/classtag inline-flex items-center gap-0 hover:gap-1.5"
                key={t.id}
              >
                {t.name}
                {!fetching && !locked ? (
                  <XMarkIcon
                    className="w-0 cursor-pointer group-hover/classtag:w-3"
                    onClick={() => {
                      updateRecipe({
                        where: { id: recipe.id },
                        data: {
                          classes: { disconnect: [{ id: t.id }] },
                        },
                      });
                      onUpdateRecipeCompleted();
                    }}
                  />
                ) : null}
              </span>
            ))
          ) : (
            <span className="tag bg-transparent text-gray-500">No Classes</span>
          ))}
        {isAddingClass && (
          <>
            <AsyncSelect
              className="radio-select w-screen max-w-xl"
              classNamePrefix="react-select"
              isSearchable={true}
              value={newClass}
              // defaultValue={unsortedClasses.map(shapeClassLinear).filter((c) => !recipe.classes.some((rc) => rc.id == c.obj.id))[0] || null}
              defaultOptions
              loadOptions={(inputValue, callback) => {
                callback(
                  searchClasses(
                    inputValue,
                    unsortedClasses?.map(shapeClass) || []
                  )
                );
              }}
              noOptionsMessage={() => "No Options"}
              maxMenuHeight={200}
              placeholder="Select a Class to Connect"
              onChange={(data) => {
                if (data?.value) {
                  setNewClass(shapeClassLinear(data.obj));
                }
              }}
              // onChange={(newValue) => setNewClass(newValue || null)}
              styles={{
                container: (provided) => ({
                  ...provided,
                  position: "sticky",
                  top: "0px",
                  marginTop: "0",
                  zIndex: 20,
                }),
                control: (styles) => ({
                  ...styles,
                  backgroundColor: "white",
                }),
                option: (styles, { data }) => ({
                  ...styles,
                  ...dot(...data.colors),
                }),
                input: (styles) => ({
                  ...styles,
                  ...dot(...(newClass?.colors || [])),
                }),
                placeholder: (styles) => ({ ...styles, ...dot() }),
                singleValue: (provided, { data }) => ({
                  ...provided,
                  ...dot(...data.colors),
                }),
              }}
            />
            {newClass ? (
              <Tooltip content="Confirm">
                <button
                  className=""
                  onClick={() => {
                    updateRecipe({
                      where: { id: recipe.id },
                      data: {
                        classes: {
                          connect: [{ id: newClass.obj.id }],
                        },
                      },
                    });
                    onUpdateRecipeCompleted();
                  }}
                >
                  <CheckIcon className="icon icon-green" />
                </button>
              </Tooltip>
            ) : null}
          </>
        )}
        {locked ? null : (
          <Tooltip content={isAddingClass ? "Cancel" : "Connect a Class"}>
            <button
              className=""
              disabled={locked}
              onClick={() => setAddClass(!isAddingClass)}
            >
              {isAddingClass ? (
                <XMarkIcon className="icon icon-gray" />
              ) : (
                <PlusIcon className="icon icon-gray" />
              )}
            </button>
          </Tooltip>
        )}
      </div>

      {/* Connected Tags */}
      <div style={{ margin: "5px" }}>
        {!isAddingTag &&
          (recipe.tags.length ? (
            recipe.tags.map((t) => (
              <span
                key={t.id}
                className="tag group/tag inline-flex items-center gap-0 hover:gap-1.5"
              >
                {t.name}
                {!fetching && !locked ? (
                  <XMarkIcon
                    className="w-0 cursor-pointer group-hover/tag:w-3"
                    onClick={() => {
                      updateRecipe({
                        where: { id: recipe.id },
                        data: {
                          tags: { disconnect: [{ id: t.id }] },
                        },
                      });
                      onUpdateRecipeCompleted();
                    }}
                  />
                ) : null}
              </span>
            ))
          ) : (
            <Notice>No Tags</Notice>
          ))}
        {isAddingTag && (
          <>
            <AncillariesComponent
              variables={{ schoolID: user.school?.id || "" }}
            >
              {({ error, fetching, data }) =>
                error ? (
                  <ErrorMessage error={error} />
                ) : fetching ? (
                  <div className="spinner" />
                ) : !isStudent(user) || user.school?.permCreateTags ? (
                  <AsyncCreatableSelect
                    placeholder="Search or Create"
                    isClearable
                    isMulti
                    isSearchable
                    autoFocus
                    noOptionsMessage={() => "No Options"}
                    value={newTags}
                    defaultOptions
                    loadOptions={(inputValue, callback) => {
                      callback(
                        searchTags(inputValue, data?.tags || [], recipe.tags)
                      );
                    }}
                    onCreateOption={(inputValue) => {
                      const newTag = {
                        value: cuid(),
                        label: inputValue,
                        __isNew__: true,
                        type: "tag",
                      } as TagOptionType;
                      setNewTags([...newTags, newTag]);
                    }}
                    onChange={(newValue) => setNewTags(newValue || [])}
                    maxMenuHeight={250}
                    styles={{
                      container: (provided) => ({
                        ...provided,
                        width: "180px",
                        display: "inline-block",
                      }),
                    }}
                  />
                ) : (
                  <AsyncSelect
                    placeholder="Search..."
                    isClearable
                    isMulti
                    isSearchable
                    autoFocus
                    value={newTags}
                    defaultOptions
                    loadOptions={(inputValue, callback) => {
                      callback(
                        searchTags(inputValue, data?.tags || [], recipe.tags)
                      );
                    }}
                    noOptionsMessage={() => "No Options"}
                    onChange={(newValue) => setNewTags(newValue || [])}
                    maxMenuHeight={250}
                    styles={{
                      container: (provided) => ({
                        ...provided,
                        width: "180px",
                        display: "inline-block",
                      }),
                    }}
                  />
                )
              }
            </AncillariesComponent>
            <Tooltip content="Confirm">
              <button
                className=""
                onClick={() => {
                  updateRecipe({
                    where: { id: recipe.id },
                    data: {
                      tags: {
                        connect: newTags
                          .filter((t) => !t.__isNew__)
                          .map((t) => ({ id: t.value })),
                        create: newTags
                          .filter((t) => t.__isNew__)
                          .map((t) => ({
                            id: t.value,
                            name: t.label,
                            school: { connect: { id: user.school?.id } },
                          })),
                      },
                    },
                  });
                  onUpdateRecipeCompleted();
                }}
              >
                <CheckIcon className="icon icon-green" />
              </button>
            </Tooltip>
          </>
        )}
        {locked ? null : (
          <Tooltip content={isAddingTag ? "Cancel" : "Add a Tag"}>
            <button
              className=""
              disabled={locked}
              onClick={() => setAddTag(!isAddingTag)}
            >
              {isAddingTag ? (
                <XMarkIcon className="icon icon-gray" />
              ) : (
                <PlusIcon className="icon icon-gray" />
              )}
            </button>
          </Tooltip>
        )}
      </div>

      <div
        className={
          user.school?.fullRecipePosition == "right" ? "grid grid-cols-2" : ""
        }
      >
        <div>
          {isTeacher(user) && !locked ? (
            <div className="my-6 flex items-center rounded border border-gray-100 bg-gray-50 p-2 shadow-inner">
              <Field className="flex w-full items-center justify-between">
                <Label className="flex flex-grow items-center" passive>
                  <span className="flex flex-col">
                    <div className="flex items-center gap-1">
                      <ExploreIcon
                        className={classNames(
                          recipe.public ? "text-blue-500" : "text-gray-500",
                          "mr-2 w-6 shrink-0 transition-colors"
                        )}
                      />
                      <span className="truncate text-lg font-medium text-gray-900">
                        {recipe.public
                          ? "This recipe is shared"
                          : "Share This Recipe"}
                      </span>
                    </div>
                    <span className="text-lg text-gray-500">
                      Other schools can use it for inspiration.
                      <Tooltip content="Shared recipes will be visible to other schools from the 'discover' page, and can also be used as a template when creating recipes.">
                        <span>
                          <InformationCircleComponent />
                        </span>
                      </Tooltip>
                    </span>
                  </span>
                </Label>
                <Switch
                  checked={recipe.public}
                  onChange={() => {
                    if (recipe.public || recipe.method) {
                      updateRecipe({
                        where: { id: recipe.id },
                        data: {
                          public: { set: !recipe.public },
                        },
                      });
                      onUpdateRecipeCompleted();
                    } else {
                      toast.error("Recipes cannot be shared without a method");
                    }
                  }}
                  className={classNames(
                    recipe.public ? "bg-blue-500" : "bg-gray-200",
                    "relative inline-flex h-5 w-8 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  )}
                >
                  <span className="sr-only">Share recipe</span>
                  <span
                    aria-hidden="true"
                    className={classNames(
                      recipe.public ? "translate-x-3" : "translate-x-0",
                      "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    )}
                  />
                </Switch>
              </Field>
            </div>
          ) : null}
          {recipe.user ? (
            <div className="my-6">
              <small>Made by: {recipe.user.name}</small>
            </div>
          ) : null}
          <div className="my-6">
            <small>Makes {recipe.servings} Servings</small>
            {isChangingServings ? (
              <div>
                <input
                  type="number"
                  autoFocus
                  min={0}
                  onChange={(e) => setServings(parseFloat(e.target.value))}
                  value={servings}
                />
                {servings ? (
                  <button
                    className="my-0 px-1.5 py-0.5"
                    disabled={fetching}
                    onClick={() => {
                      updateRecipe({
                        where: { id: recipe.id },
                        data: {
                          servings: { set: servings },
                          recipeIngredients: {
                            update: recipe.recipeIngredients
                              .map((ingredient) => {
                                const {
                                  id,
                                  amount,
                                  unit,
                                  ingredient: {
                                    measurement,
                                    units: customUnits,
                                    density,
                                  },
                                } = ingredient;

                                const units = combineUnits({
                                  measurement,
                                  units: customUnits,
                                  density,
                                });

                                const appUnit = getUnitOrFallback(units, unit);
                                if (!appUnit) return null;
                                const relAmount = toRelativeAmount(
                                  amount,
                                  appUnit,
                                  density || undefined,
                                  recipe.servings
                                );

                                return {
                                  where: { id },
                                  data: {
                                    amount: {
                                      set: toAbsoluteAmount(
                                        relAmount,
                                        appUnit,
                                        density || undefined,
                                        servings
                                      ),
                                    },
                                  },
                                };
                              })
                              .filter(
                                (ri): ri is NonNullable<typeof ri> => !!ri
                              ),
                          },
                        },
                      });
                      onUpdateRecipeCompleted();
                    }}
                  >
                    <CheckIcon className="icon icon-green" />
                  </button>
                ) : null}
                <button className="" onClick={() => setServingsChange(false)}>
                  <XMarkIcon className="icon icon-gray" />
                </button>
              </div>
            ) : locked ? null : (
              <button
                className=""
                disabled={locked}
                onClick={() => setServingsChange(true)}
              >
                <PencilIcon className="icon icon-gray" />
              </button>
            )}
          </div>
          <div className="my-6">
            <small>
              To be made
              <select
                name="madeInGroups"
                id="madeIngroups"
                value={madeInGroupsValue || ""}
                onChange={(e) => {
                  const tempValue =
                    e.target.value == "unselected"
                      ? null
                      : (e.target.value as Recipe_MadeInGroups);

                  setMadeInGroupsValue(tempValue);

                  updateRecipe({
                    where: { id: recipe.id },
                    data: {
                      madeInGroups: {
                        set: tempValue || null,
                      },
                    },
                  });
                }}
                className="ml-1.5 rounded border border-solid border-gray-300 p-1.5"
                disabled={fetching || locked}
              >
                <option value="unselected">(no option)</option>
                <option value={Recipe_MadeInGroups.Individuals}>
                  individually
                </option>
                <option value={Recipe_MadeInGroups.Pairs}>in pairs</option>
                <option value={Recipe_MadeInGroups.Trios}>
                  in groups of three
                </option>
                <option value={Recipe_MadeInGroups.Four}>
                  in groups of four
                </option>
                <option value={Recipe_MadeInGroups.Five}>
                  in groups of five
                </option>
                <option value={Recipe_MadeInGroups.Six}>
                  in groups of six
                </option>
              </select>
            </small>
            <small className="mt-1 block max-w-3xl rounded bg-blue-50 p-1.5 font-normal text-blue-600">
              Note: This does not affect orders, it is only for informational
              purposes. If you want to change how much this recipe produces,
              update the servings or the ingredients themselves.
            </small>
          </div>
          {cost > 0 ? (
            <small className="flex items-center">
              <BanknotesIcon className="mr-2 w-6 text-stone-600" />
              <div>
                <div
                  className={`m-1.5 font-medium ${
                    user.school?.fullRecipePosition == "right"
                      ? ""
                      : "inline-block"
                  }`}
                >
                  ${cost} for {recipe.servings} Servings
                </div>
                <div
                  className={`m-1.5 ${
                    user.school?.fullRecipePosition == "right"
                      ? ""
                      : "inline-block"
                  }`}
                >
                  (${(cost / recipe.servings).toFixed(2)} per Serving)
                </div>
                <div
                  className={classNames(
                    "tag m-1.5 py-0.5",
                    user.school?.fullRecipePosition == "right"
                      ? ""
                      : "inline-block",
                    costedIngredientPercentage >= 90
                      ? "tag-green"
                      : costedIngredientPercentage >= 75
                      ? "tag-cyan"
                      : costedIngredientPercentage >= 50
                      ? "tag-orange"
                      : "tag-red"
                  )}
                >
                  {costedIngredientCount}/{recipe.recipeIngredients.length} (
                  {costedIngredientPercentage}%){" "}
                  <Tooltip
                    content={`${costedIngredientCount} out of ${
                      recipe.recipeIngredients.length
                    } ingredients are costed (${costedIngredientPercentage}%)${
                      costedIngredientPercentage < 90
                        ? ". For a more accurate recipe costing, consider adding cost information for all ingredients."
                        : ""
                    }`}
                  >
                    <span>
                      <InformationCircleIcon className="size-6 cursor-help" />
                    </span>
                  </Tooltip>
                </div>
                {abovePriceLimit ? (
                  <Tooltip
                    content={`This order is over the price limit. The limit is $${(
                      priceLimit.price / 100
                    ).toFixed(2)} per${
                      priceLimit.amount > 1 ? ` ${priceLimit.amount}` : ""
                    } ${priceLimit.range}${priceLimit.amount > 1 ? "s" : ""}${
                      priceLimit.amount > 1 ||
                      priceLimit.range === PriceWarning_Range.Recipe
                        ? ` ($${unitaryPriceLimit.toFixed(2)} per serving)`
                        : ""
                    }.`}
                  >
                    <span className="ml-1.5 inline-block">
                      <ExclamationTriangleIcon className="w-6 animate-wiggle text-orange-600" />
                    </span>
                  </Tooltip>
                ) : null}
              </div>
            </small>
          ) : null}
        </div>
        {user.school?.fullRecipePosition == "right" ? (
          <div>
            <RecipeImage
              {...{
                recipe,
                fetching,
                user,
                updateRecipe,
                onUpdateRecipeCompleted,
              }}
            />
          </div>
        ) : null}
      </div>
      {user.school?.fullRecipePosition == "center" ? (
        <RecipeImage
          {...{
            recipe,
            fetching,
            user,
            updateRecipe,
            onUpdateRecipeCompleted,
          }}
        />
      ) : null}
      {user.school?.fullRecipePosition == "top" ? (
        <HeadlessRecipeImage
          {...{
            recipe,
            fetching,
            user,
            updateRecipe,
            onUpdateRecipeCompleted,
          }}
        />
      ) : null}
      <div>
        <div className="mt-8">
          {recipe.recipeIngredients.length ? (
            <DragNDrop
              collection={UUIDfy(recipeSections)}
              itemKey="recipeIngredients"
              setCollection={async (newRecipeSections) => {
                setReorderPulse(true);

                const recipeIngredients =
                  getRecipeIngredients(newRecipeSections);

                await updateRecipe({
                  where: { id: recipe.id },
                  data: {
                    recipeSections: {
                      update: newRecipeSections
                        .filter((rs) => rs.id !== "sectionLess")
                        .map((rs, index) => ({
                          where: { id: rs.id },
                          data: {
                            position: { set: index + 1 },
                          },
                        })),
                    },
                    recipeIngredients: {
                      update: recipeIngredients.map((ri, index) => {
                        // const oldRi = oldRecipeIngredients.find(
                        //   (ori) => ori.id === ri.id
                        // );

                        return {
                          where: { id: ri.id },
                          data: {
                            position: { set: index },
                            recipeSection: ri.recipeSection?.id
                              ? {
                                  connect: {
                                    id: ri.recipeSection.id,
                                  },
                                }
                              : {
                                  disconnect: {
                                    id: { contains: "" }, // just get it to disconnect
                                  },
                                },
                            // : oldRi?.recipeSection?.id
                            // ? {
                            //     // disconnect: true,
                            //     disconnect: {
                            //       id: { contains: "" }, // just get it to disconnect
                            //     },
                            //   }
                            // : undefined,
                          },
                        };
                      }),
                    },
                  },
                });

                setReorderPulse(false);
              }}
            >
              {(recipeSections) => (
                <div>
                  <h3 className="font-semibold">Ingredients</h3>
                  <div
                    className={classNames(
                      "space-y-1.5",
                      reorderPulse ? "animate-pulse" : ""
                    )}
                  >
                    {recipeSections.map((section, index) => (
                      <RecipeSectionComponent
                        key={section.id || "sectionLess"}
                        user={user}
                        index={index}
                        recipe={recipe}
                        section={section}
                        updateRecipe={updateRecipe}
                        fetching={fetching}
                      />
                    ))}
                  </div>
                  <div className="group -ml-3 flex max-w-xl items-center gap-2.5 pb-8 pt-3">
                    {isAddingSection ? (
                      <div className="flex items-center">
                        <input
                          type="text"
                          autoFocus
                          value={sectionName}
                          placeholder="Section Name"
                          onChange={(e) => setSectionName(e.target.value)}
                          className="rounded border border-solid border-gray-300 p-1.5"
                          disabled={fetching}
                        />
                        {sectionName ? (
                          <button
                            className="btn btn-primary"
                            onClick={async () => {
                              await updateRecipe({
                                where: { id: recipe.id },
                                data: {
                                  recipeSections: {
                                    create: [
                                      {
                                        name: sectionName,
                                        position:
                                          recipe.recipeSections.length + 1,
                                      },
                                    ],
                                  },
                                },
                              });

                              setAddingSection(false);
                              setSectionName("");
                            }}
                            disabled={fetching}
                          >
                            <CheckCircleIcon className="mr-0.5 w-5 text-green-700" />
                            Confirm
                          </button>
                        ) : null}
                        <button
                          className="btn"
                          onClick={() => {
                            setAddingSection(false);
                            setSectionName("");
                          }}
                          disabled={fetching}
                        >
                          <XCircleIcon className="mr-0.5 w-5 text-gray-700" />
                          Cancel
                        </button>
                      </div>
                    ) : locked ? null : (
                      <button
                        className="flex items-center gap-1"
                        onClick={() => setAddingSection(true)}
                      >
                        <PlusCircleIcon className="w-5 transform-gpu text-gray-700 transition group-hover:scale-125 group-hover:text-gray-900" />
                        <span className="font-semibold text-gray-700 transition group-hover:text-gray-800">
                          Add a Section
                        </span>
                      </button>
                    )}
                    <hr className="flex-1 border-gray-300" />
                  </div>
                </div>
              )}
            </DragNDrop>
          ) : (
            <p>A recipe with no ingredients? How odd. 🤔</p>
          )}
          {isAddingIngredient ? (
            <form
              className={classNames(
                "group m-2.5 mx-auto flex w-full max-w-2xl flex-col items-center justify-center rounded-lg border border-solid border-cyan-100 p-2.5",
                isCreatingIngredients
                  ? ""
                  : "bg-gradient-to-tr from-cyan-50 to-green-50"
              )}
              onSubmit={async (e) => {
                e.preventDefault();

                console.log("There was a submit");

                if (!newIngredient || !newIngredientUnit) {
                  return;
                }

                if (!unitExists) {
                  toast.error(
                    `The selected unit, "${newIngredientUnit.name}", does not exist on this ingredient. Please change the unit.`
                  );
                  return;
                }

                if (!stringIsValidNumber(String(newIngredientAmount))) {
                  toast.error(
                    "Please enter a valid amount. Accepted inputs can be a whole number, decimal or fraction."
                  );

                  return;
                }

                await updateRecipe({
                  where: { id: recipe.id },
                  data: {
                    recipeIngredients: {
                      create: [
                        {
                          position:
                            getRecipeIngredients(recipeSections).length + 1,
                          amount: toAbsoluteAmount(
                            stringToNumber(String(newIngredientAmount)),
                            newIngredientUnit,
                            newIngredient.density || undefined,
                            recipe.servings
                          ),
                          ingredient: {
                            connect: {
                              id: newIngredient.id,
                            },
                          },
                          unit: {
                            connect: {
                              id: newIngredientUnit.id,
                            },
                          },
                          preparation: newIngredientPreparation,
                          recipeSection:
                            recipeSections.length > 1 &&
                            recipeSections[recipeSections.length - 1].id !==
                              "sectionLess"
                              ? {
                                  connect: {
                                    id: recipeSections[
                                      recipeSections.length - 1
                                    ].id,
                                  },
                                }
                              : undefined,
                        },
                      ],
                    },
                  },
                }).then(({ error }) => {
                  if (error) {
                    toast.error(error.message);
                    return;
                  }

                  onUpdateRecipeCompleted();

                  setAddingIngredient(false);
                  setNewIngredient(null);
                  setNewIngredientAmount(1);
                  setNewIngredientUnit(null);
                  setNewIngredientPreparation("");
                  setCreatingIngredients(false);

                  // refocus the add ingredient button
                  // helps when adding multiple ingredients
                  setTimeout(() => {
                    if (addIngredientButton.current != document.activeElement) {
                      addIngredientButton.current?.focus();
                    }
                  }, 250);
                });
              }}
            >
              <div className="flex w-full items-center justify-between">
                <AsyncSelectIngredient
                  value={
                    newIngredient ? ingredientToOption(newIngredient) : null
                  }
                  externalRef={addIngredientInput}
                  onChange={(value) => {
                    if (value) {
                      setNewIngredient(value);
                      setNewIngredientUnit(
                        getBaseUnit(
                          combineUnits(value),
                          value.selectionUnit || undefined
                        )
                      );

                      // select the amount input
                      // for faster input
                      setTimeout(() => {
                        newIngredientAmountInput.current?.select();
                        newIngredientAmountInput.current?.focus();
                      }, 250);
                    }
                  }}
                  onCreated={(ingredients) => {
                    setNewIngredient(ingredients[0]);
                    setNewIngredientUnit(
                      getBaseUnit(
                        combineUnits(ingredients[0]),
                        ingredients[0].selectionUnit || undefined
                      )
                    );

                    // select the amount input
                    // for faster input
                    setTimeout(() => {
                      newIngredientAmountInput.current?.select();
                      newIngredientAmountInput.current?.focus();
                    }, 250);
                  }}
                  placeholder="Apple..."
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 20,
                    }),
                  }}
                  className="flex-1"
                />
                {newIngredient ? (
                  <>
                    <Tooltip content="Edit Ingredient">
                      <button type="button" onClick={() => open()}>
                        <PencilIcon className="icon icon-gray opacity-25 group-hover:opacity-100" />
                      </button>
                    </Tooltip>
                    <DialogComponent
                      isOpen={editDialog}
                      onClose={close}
                      aria-label="Edit ingredient"
                    >
                      <h2 className="mb-8">Edit {newIngredient.name}</h2>
                      <InventoryGrid itemHeight="90px" itemWidth="400px">
                        <IngredientsGridItem
                          user={user}
                          ingredient={newIngredient}
                          isFragment={true}
                        />
                      </InventoryGrid>
                    </DialogComponent>
                  </>
                ) : null}
              </div>
              {newIngredient ? (
                <div className="flex w-full items-center gap-2 py-2 pr-8 text-sm">
                  <div className="relative">
                    <input
                      ref={newIngredientAmountInput}
                      type="text"
                      onChange={(e) => setNewIngredientAmount(e.target.value)}
                      value={newIngredientAmount ?? ""}
                      required={true}
                      className={classNames(
                        "input w-12",
                        stringIsValidNumber(String(newIngredientAmount))
                          ? "border-gray-400"
                          : "border-orange-400 outline-orange-600"
                      )}
                      autoFocus
                    />
                    {stringIsValidNumber(String(newIngredientAmount)) ? null : (
                      <Tooltip
                        content={`"${newIngredientAmount}" is not a valid input`}
                      >
                        <span>
                          <ExclamationTriangleIcon className="icon icon-orange absolute right-0 top-0 mt-6" />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <Select
                    className="radio-select"
                    classNamePrefix="react-select"
                    defaultValue={unitToOption(
                      getBaseUnit(
                        combineUnits(newIngredient),
                        newIngredient.selectionUnit || undefined
                      ) as NonNullable<
                        IngredientQuery["ingredient"]
                      >["units"][number]
                    )}
                    options={getUnitOptions(
                      combineUnits(newIngredient),
                      newIngredient.density || undefined
                    )}
                    noOptionsMessage={() => "No Options"}
                    onChange={(data) => {
                      if (!data) return;

                      const tempUnit = combineUnits(newIngredient).find(
                        (u) => u.id === data.value
                      );
                      if (!tempUnit) return;

                      setNewIngredientUnit(tempUnit);
                    }}
                    styles={{
                      container: (provided) => ({
                        ...provided,
                        width: "175px",
                      }),
                    }}
                  />
                  {!unitExists ? (
                    <Tooltip
                      content={`The selected unit, "${newIngredientUnit?.name}", does not exist on this ingredient. Please change the unit.`}
                    >
                      <span>
                        <ExclamationTriangleIcon className="icon icon-orange" />
                      </span>
                    </Tooltip>
                  ) : null}
                  <input
                    type="text"
                    value={newIngredientPreparation}
                    onChange={(e) =>
                      setNewIngredientPreparation(e.target.value)
                    }
                    placeholder="grated, sliced..."
                    className="input flex-1"
                  />
                </div>
              ) : (
                // <div className="w-full text-center">
                // </div>
                <div
                  style={{
                    textAlign: "center",
                    margin: "25px auto",
                  }}
                >
                  {isCreatingIngredients ||
                  locked ||
                  !canCreateIngredients ? null : (
                    <button
                      disabled={locked}
                      onClick={() => setCreatingIngredients(true)}
                      className="btn"
                    >
                      Or Create a New One
                    </button>
                  )}
                  {isCreatingIngredients && user.school ? (
                    <div
                      className={classNames(
                        isCreatingIngredients
                          ? "my-2.5 rounded border border-solid border-cyan-200 bg-gradient-to-tr from-cyan-50 to-green-50 p-2.5"
                          : ""
                      )}
                    >
                      <button
                        disabled={locked}
                        onClick={() => {
                          setCreatingIngredients(false);

                          if (
                            addIngredientInput.current != document.activeElement
                          ) {
                            addIngredientInput.current?.focus();
                          }
                        }}
                        className="btn btn-warning"
                      >
                        Cancel
                      </button>
                      <CreateIngredients
                        school={user.school}
                        onCompleted={() => {
                          // remove the create ingredients section
                          setCreatingIngredients(false);

                          if (
                            addIngredientInput.current != document.activeElement
                          ) {
                            addIngredientInput.current?.focus();
                          }

                          // Let the user know: created but will take a while to show up in search
                          toast(
                            "The created ingredients may take a few seconds to show up in search results."
                          );
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              )}
              <div className="mt-5">
                {newIngredient ? (
                  <button
                    type="submit"
                    disabled={fetching}
                    className="btn btn-primary"
                  >
                    <CheckCircleIcon className="mr-0.5 w-5 text-green-800" />{" "}
                    Confirm
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setAddingIngredient(false);
                    setNewIngredient(null);
                    setNewIngredientAmount(1);
                    setNewIngredientUnit(null);
                    setNewIngredientPreparation("");
                  }}
                >
                  <XCircleIcon className="mr-0.5 w-5 text-gray-900" /> Cancel
                </button>
              </div>
            </form>
          ) : locked ? null : (
            <button
              ref={addIngredientButton}
              className="btn"
              disabled={locked}
              onClick={() => setAddingIngredient(true)}
            >
              Add an Ingredient
            </button>
          )}
        </div>
      </div>
      <div>
        <div className="flex flex-row items-center justify-between">
          <h3>Method</h3>
          {isChangingMethod || locked ? null : (
            <Tooltip content="Edit Method">
              <button
                className="my-0 px-1.5 py-0.5"
                disabled={locked}
                onClick={() => setMethodChange(true)}
              >
                <PencilIcon className="w-5 text-gray-800" />
              </button>
            </Tooltip>
          )}
        </div>
        {isChangingMethod ? (
          <TextEditor
            content={
              recipe.method
                ?.split(/\n/)
                .map((value, index, arr) =>
                  arr.length > 1 ? `<p>${value}</p>` : value
                )
                .join("") || ""
            }
            onConfirm={(content) => {
              updateRecipe({
                where: { id: recipe.id },
                data: {
                  method: { set: content || null },
                },
              });
              onUpdateRecipeCompleted();
            }}
            onCancel={() => setMethodChange(false)}
          />
        ) : (
          <TextContentWrapper
            className="markdown rounded-lg border border-solid border-gray-300 bg-gray-100 p-1.5 shadow-lg"
            dangerouslySetInnerHTML={{
              __html:
                recipe.method
                  ?.split(/\n/)
                  .map((value, index, arr) =>
                    arr.length > 1 ? `<p>${value}</p>` : value
                  )
                  .join("") || "No Method",
            }}
          ></TextContentWrapper>
        )}
      </div>
      <div>
        <h3>Extra Info</h3>
        {recipe.recipeInformation.length ? (
          <>
            <ul>
              {recipe.recipeInformation.map((information) => (
                <InfoItem
                  key={information.id}
                  information={information}
                  recipeID={recipe.id}
                  fetching={fetching}
                  updateRecipe={updateRecipe}
                />
              ))}
            </ul>
          </>
        ) : (
          <>
            <small>No Extra Info</small>
          </>
        )}
        {isAddingInfo ? (
          <form
            className="my-5"
            style={{ width: "400px" }}
            onSubmit={(e) => {
              e.preventDefault();

              updateRecipe({
                where: { id: recipe.id },
                data: {
                  recipeInformation: {
                    create: [
                      {
                        contents: info,
                      },
                    ],
                  },
                },
              });
              onUpdateRecipeCompleted();
            }}
          >
            <Input
              value={info}
              style={{ width: "350px" }}
              autoFocus
              onChange={(e) => setInfo(e.target.value)}
              required
            />
            <div className="flex items-center">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={fetching}
              >
                <CheckCircleIcon className="mr-0.5 w-5 text-green-800" />{" "}
                Confirm
              </button>
              <button
                className="btn btn-warning text-black"
                onClick={() => setAddingInfo(false)}
              >
                <XCircleIcon className="mr-0.5 w-5 text-gray-900" /> Cancel
              </button>
            </div>
          </form>
        ) : locked ? null : (
          <button
            className="btn"
            disabled={locked}
            onClick={() => setAddingInfo(true)}
          >
            <PlusCircleIcon className="mr-0.5 w-5 text-gray-800" /> Add
            Information
          </button>
        )}
      </div>
      <div className="sticky bottom-0 flex items-center justify-center">
        <button
          className="btn"
          title="Show Less"
          onClick={() => setExpand(false)}
        >
          <ChevronUpIcon className="w-5" />
        </button>
      </div>
      {fetching ? <div className="spinner absolute bottom-2 right-2" /> : null}
    </div>
  );
};

interface MainRecipeActionsProps {
  user: CurrentUser;
  recipe: NonNullable<RecipeQuery["recipe"]>;
  setDeleted: Dispatch<SetStateAction<boolean>>;
  setArchived: Dispatch<SetStateAction<boolean>>;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  folder:
    | NonNullable<CurrentUser["school"]>["recipeCollections"][0]
    | null
    | undefined;
  locked: boolean;
  recipeSections: ReturnType<typeof getRecipeSections>;
  isNative?: boolean;
  setSelectedRecipes?: Dispatch<SetStateAction<string[]>>;
}

export const MainRecipeActions: React.FC<MainRecipeActionsProps> = ({
  user,
  recipe,
  setDeleted,
  setArchived,
  sortedFolders,
  folder,
  locked,
  recipeSections,
  isNative,
  setSelectedRecipes,
}) => {
  const router = useRouter();

  const [moveDialog, setMoveDialog] = useState(false);
  const openMove = () => setMoveDialog(true);
  const closeMove = () => setMoveDialog(false);

  const [{ fetching: fetchingOne, error: errorOne }, updateRecipe] =
    useUpdateRecipeMutation();
  const [{ fetching: fetchingTwo, error: errorTwo }, createManyRecipes] =
    useCreateManyRecipesMutation();

  const fetching = fetchingOne || fetchingTwo;
  const error = errorOne || errorTwo;

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center">
        {user.school && !(isStudent(user) && !user.school.permMakeOrders) ? (
          <OrderDialogContext.Consumer>
            {({ setOrderDialog, setRecipeToOrder }) => (
              <button
                className="btn btn-primary"
                onClick={() => {
                  setRecipeToOrder(recipe.id);
                  setOrderDialog(true);
                }}
              >
                <ShoppingCartIcon className="mr-0.5 w-5 text-green-800" /> Order
              </button>
            )}
          </OrderDialogContext.Consumer>
        ) : null}
        <a
          href={`https://recipe.classcart.com/?id=${recipe.id}`}
          target="_blank"
          rel="noreferrer"
        >
          <button className="btn bg-white hover:bg-gray-200">
            <PrinterIcon className="mr-0.5 w-5" /> Print
          </button>
        </a>
        <Popover className="relative">
          {({ open, close }) => (
            <>
              <PopoverButton
                className={`
                ${open ? "" : "text-opacity-90"}
                btn group hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75`}
              >
                <span>More</span>
                <ChevronDownIcon
                  className={`${open ? "" : "text-opacity-70"}
                  ml-1.5 w-4 text-gray-800 transition group-hover:text-opacity-80`}
                  aria-hidden="true"
                />
              </PopoverButton>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-0.5"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-0.5"
              >
                <PopoverPanel className="absolute left-1/2 z-10 mt-2 w-screen max-w-sm -translate-x-1/2 transform px-2.5 lg:max-w-3xl sm:px-0">
                  <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                    <div className="relative grid gap-5 bg-white p-2.5 lg:grid-cols-2">
                      {/* Duplicate Recipe */}
                      <button
                        className="visible -m-2 flex items-center rounded-lg p-1.5 transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                        onClick={async () => {
                          const recipeId = cuid();

                          let ingredientPosition = 1;

                          // Boolean to know which sections is... sectionless
                          const sections: [
                            boolean,
                            RecipeSectionCreateWithoutRecipeInput
                          ][] = recipeSections.map<
                            [boolean, RecipeSectionCreateWithoutRecipeInput]
                          >(({ id, name, recipeIngredients }, index) => [
                            id !== "sectionLess",
                            {
                              id: cuid(),
                              name,
                              position: index + 1,
                              recipeIngredients: {
                                create: recipeIngredients.map(
                                  ({
                                    amount,
                                    unit,
                                    preparation,
                                    ingredient,
                                  }) => ({
                                    id: cuid(),
                                    recipe: {
                                      connect: { id: recipeId },
                                    },
                                    position: ++ingredientPosition,
                                    amount: amount,
                                    preparation,
                                    ingredient: {
                                      connect: { id: ingredient.id },
                                    },
                                    unit: {
                                      connect: { id: unit.id },
                                    },
                                  })
                                ),
                              },
                            },
                          ]);

                          await toast.promise(
                            createManyRecipes({
                              schoolID: user.school?.id || "",
                              recipes: [
                                {
                                  id: recipeId,
                                  name: `${recipe.name} (copy)`,
                                  public: recipe.public,
                                  process: "duplicated",
                                  servings: recipe.servings,
                                  madeInGroups: recipe.madeInGroups,
                                  image: recipe.image,
                                  method: recipe.method,
                                  recipeInformation: {
                                    create: recipe.recipeInformation.map(
                                      ({ contents }) => ({
                                        contents,
                                      })
                                    ),
                                  },
                                  recipeIngredients: {
                                    create: sections
                                      .find(([isSection]) => !isSection)?.[1]
                                      ?.recipeIngredients?.create?.map(
                                        (ri) => ({
                                          ...ri,
                                          recipe: undefined,
                                        })
                                      ),
                                  },
                                  recipeSections: {
                                    create: sections
                                      .filter(([isSection]) => isSection)
                                      .map(([, section]) => section),
                                  },
                                  user: {
                                    connect: { id: user.id },
                                  },
                                  tags: {
                                    connect: recipe.tags.map((tag) => ({
                                      id: tag.id,
                                    })),
                                  },
                                },
                              ],
                            }),
                            {
                              loading: "Copying...",
                              success: (
                                <span className="font-normal">
                                  Recipe duplicated.
                                </span>
                              ),
                              error: (
                                <span className="font-normal">
                                  Error duplicating recipe
                                </span>
                              ),
                            }
                          );

                          setTimeout(() => {
                            router.push(`/recipes#${recipeId}`);
                          }, 2.5 * 1000);
                        }}
                      >
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-gray-500 sm:h-8 sm:w-8">
                          {fetching ? (
                            <div className="spinner" />
                          ) : (
                            <DocumentDuplicateIcon aria-hidden="true" />
                          )}
                        </div>
                        <div className="ml-2.5 text-left">
                          <p className="text-sm font-medium text-gray-900">
                            Duplicate
                          </p>
                          <p className="text-sm text-gray-500">
                            Create a copy of this recipe
                          </p>
                        </div>
                      </button>

                      {/* Move To Folder */}
                      <button
                        className="visible -m-2 flex items-center rounded-lg p-1.5 transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                        disabled={locked}
                        onClick={openMove}
                      >
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-gray-500 sm:h-8 sm:w-8">
                          {fetching ? (
                            <div className="spinner" />
                          ) : (
                            <FolderIcon aria-hidden="true" />
                          )}
                        </div>
                        <div className="ml-2.5 text-left">
                          <p className="text-sm font-medium text-gray-900">
                            Move to Folder
                          </p>
                          <p className="text-sm text-gray-500">
                            Move this recipe to a different folder
                          </p>
                        </div>
                      </button>

                      {/* Archive Recipe */}
                      <button
                        className="visible -m-2 flex items-center rounded-lg p-1.5 transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                        disabled={locked}
                        onClick={async () => {
                          await toast.promise(
                            updateRecipe({
                              where: { id: recipe.id },
                              data: {
                                archivedAt: { set: new Date() },
                              },
                            }),
                            {
                              loading: "Archiving...",
                              success: (
                                <span className="font-normal">
                                  The recipe &quot;{recipe.name}&quot; has been
                                  archived.
                                </span>
                              ),
                              error: (
                                <span className="font-normal">
                                  Error archiving recipe &quot;{recipe.name}
                                  &quot;
                                </span>
                              ),
                            }
                          );

                          setArchived(true);
                        }}
                      >
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-gray-500 sm:h-8 sm:w-8">
                          {fetching ? (
                            <div className="spinner" />
                          ) : (
                            <ArchiveBoxIcon aria-hidden="true" />
                          )}
                        </div>
                        <div className="ml-2.5 text-left">
                          <p className="text-sm font-medium text-gray-900">
                            Archive
                          </p>
                          <p className="text-sm text-gray-500">
                            Remove this recipe from the main collection. You
                            will not be able to order it while it is archived.
                          </p>
                        </div>
                      </button>

                      {/* Lock Recipe For Teachers */}
                      {isTeacher(user) ? (
                        recipe.locked && recipe.lockedBy ? (
                          <button
                            className="visible -m-2 flex items-center rounded-lg p-1.5 transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                            onClick={async () => {
                              await toast.promise(
                                updateRecipe({
                                  where: { id: recipe.id },
                                  data: {
                                    locked: { set: false },
                                    lockedBy: {
                                      disconnect: {
                                        id: { equals: recipe.lockedBy?.id },
                                      },
                                    },
                                  },
                                }),
                                {
                                  loading: "Unlocking...",
                                  success: (
                                    <span className="font-normal">
                                      The recipe &quot;{recipe.name}&quot; has
                                      been unlocked for teachers (& students).
                                    </span>
                                  ),
                                  error: (
                                    <span className="font-normal">
                                      Error unlocking recipe &quot;
                                      {recipe.name}
                                      &quot;
                                    </span>
                                  ),
                                }
                              );
                            }}
                          >
                            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-gray-500 sm:h-8 sm:w-8">
                              {fetching ? (
                                <div className="spinner" />
                              ) : (
                                <LockOpenIcon aria-hidden="true" />
                              )}
                            </div>
                            <div className="ml-2.5 text-left">
                              <p className="text-sm font-medium text-gray-900">
                                Unlock Recipe
                              </p>
                              <p className="text-sm text-gray-500">
                                {recipe.lockedBy.id === user.id ? (
                                  "Unlock this recipe for teachers. This will also unlock it for students."
                                ) : (
                                  <>
                                    This recipe was locked by{" "}
                                    <span className="font-medium text-cyan-600">
                                      {recipe.lockedBy.name}
                                    </span>
                                    . An email will be sent to them when it is
                                    unlocked.
                                  </>
                                )}
                              </p>
                            </div>
                          </button>
                        ) : (
                          <button
                            className="visible -m-2 flex items-center rounded-lg p-1.5 transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                            onClick={async () => {
                              await toast.promise(
                                updateRecipe({
                                  where: { id: recipe.id },
                                  data: {
                                    locked: { set: true },
                                    lockedBy: { connect: { id: user.id } },
                                  },
                                }).then(({ error }) => {
                                  if (!error) {
                                    close();
                                  }
                                }),
                                {
                                  loading: "Locking...",
                                  success: (
                                    <span className="font-normal">
                                      The recipe &quot;{recipe.name}&quot; has
                                      been locked for teachers (& students).
                                    </span>
                                  ),
                                  error: (
                                    <span className="font-normal">
                                      Error locking recipe &quot;
                                      {recipe.name}
                                      &quot;
                                    </span>
                                  ),
                                }
                              );
                            }}
                          >
                            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-gray-500 sm:h-8 sm:w-8">
                              {fetching ? (
                                <div className="spinner" />
                              ) : (
                                <LockClosedIcon aria-hidden="true" />
                              )}
                            </div>
                            <div className="ml-2.5 text-left">
                              <p className="text-sm font-medium text-gray-900">
                                Lock For Teachers{" "}
                                <span className="tag tag-primary">new</span>
                              </p>
                              <p className="text-sm text-gray-500">
                                Lock this recipe for teachers. This will also
                                lock it for students.
                              </p>
                            </div>
                          </button>
                        )
                      ) : null}
                    </div>
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
        <MoveRecipesToFolderDialog
          user={user}
          folder={folder}
          sortedFolders={sortedFolders}
          isOpen={moveDialog}
          onClose={closeMove}
          recipes={[recipe]}
        />
      </div>

      <div className="flex items-center gap-1.5">
        {recipe.locked ? (
          isTeacher(user) ? (
            <Tooltip
              content={
                <div className="font-normal">
                  <h2 className="mb-2 text-white">This Recipe Is Locked</h2>
                  {recipe.lockedBy ? (
                    <p>
                      Press this button to unlock it. This recipe was locked by{" "}
                      <span className="font-medium text-cyan-600">
                        {recipe.lockedBy.name}
                      </span>
                      . An email will be sent to them when it is unlocked.
                    </p>
                  ) : (
                    <p>Press this button to unlock it for students.</p>
                  )}
                </div>
              }
            >
              <button
                className=""
                disabled={fetching}
                onClick={async () => {
                  if (recipe.lockedBy) {
                    await updateRecipe({
                      where: { id: recipe.id },
                      data: {
                        locked: { set: false },
                        lockedBy: {
                          disconnect: {
                            id: { equals: recipe.lockedBy?.id },
                          },
                        },
                      },
                    });
                  } else {
                    await updateRecipe({
                      where: { id: recipe.id },
                      data: {
                        locked: { set: false },
                      },
                    });
                  }
                }}
              >
                {fetching ? (
                  <div className="spinner h-6 w-6" />
                ) : (
                  <LockClosedIcon className="m-1.5 w-6 text-gray-800 hover:text-gray-900" />
                )}
              </button>
            </Tooltip>
          ) : (
            <Tooltip
              content={
                <div className="font-normal">
                  <h2 className="mb-2 text-white">This Recipe Is Locked</h2>
                  <p>
                    Locked recipes are read only and cannot be edited by
                    students.
                  </p>
                </div>
              }
            >
              <LockClosedIcon className="m-1.5 w-6 text-gray-800 hover:text-gray-900" />
            </Tooltip>
          )
        ) : isTeacher(user) ? (
          <Tooltip
            content={
              <div className="font-normal">
                <h2 className="mb-2 text-white">Lock This Recipe</h2>
                <p>
                  Locking a recipe makes it read only for students and prevents
                  them making changes. They can still duplicate the recipe.
                </p>
              </div>
            }
          >
            <button
              disabled={fetching}
              onClick={async () => {
                await updateRecipe({
                  where: { id: recipe.id },
                  data: {
                    locked: { set: true },
                  },
                });
              }}
            >
              {fetching ? (
                <div className="spinner h-6 w-6" />
              ) : (
                <LockOpenIcon className="m-1.5 w-6 text-gray-800 hover:text-gray-900" />
              )}
            </button>
          </Tooltip>
        ) : null}

        {isNative ? (
          <DeleteRecipe
            recipe={recipe}
            user={user}
            setDeleted={setDeleted}
            setSelectedRecipes={setSelectedRecipes}
          />
        ) : null}
      </div>
    </div>
  );
};

interface RecipeImageProps {
  recipe: NonNullable<RecipeQuery["recipe"]>;
  fetching: boolean;
  user: CurrentUser;
  updateRecipe: UseMutationExecute<
    UpdateRecipeMutation,
    Exact<{
      where: RecipeWhereUniqueInput;
      data: RecipeUpdateInput;
    }>
  >;
  onUpdateRecipeCompleted: () => void;
}

const RecipeImage: React.FC<RecipeImageProps> = ({
  recipe,
  fetching,
  user,
  updateRecipe,
  onUpdateRecipeCompleted,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);

  const locked = isRecipeLocked(recipe, user);

  return (
    <div>
      {recipe.image ? (
        <div className="relative mx-auto w-max">
          <img
            src={recipe.image}
            alt={`Image of ${recipe.name}`}
            className="mx-auto my-5 block rounded-lg"
            style={{
              maxWidth:
                user.school?.fullRecipePosition == "center" ? "300px" : "200px",
              maxHeight:
                user.school?.fullRecipePosition == "center" ? "300px" : "200px",
              minWidth: "150px",
              minHeight: "150px",
              boxShadow: "#a0a0a0 0px 0px 5px",
            }}
          />
          {locked ? null : (
            <>
              <Tooltip content="Replace Image">
                <button
                  className="btn absolute bottom-0 left-0"
                  onClick={open}
                  disabled={locked}
                >
                  <ArrowPathIcon className="w-6" />
                </button>
              </Tooltip>
              <Tooltip content="Remove Image">
                <button
                  className="btn btn-warning absolute bottom-0 right-0"
                  disabled={locked}
                  onClick={() => {
                    updateRecipe({
                      where: { id: recipe.id },
                      data: {
                        image: { set: null },
                      },
                    });
                    onUpdateRecipeCompleted();
                  }}
                >
                  <TrashIcon className="w-6" />
                </button>
              </Tooltip>
            </>
          )}
        </div>
      ) : (
        <div className="relative mx-auto w-max rounded-lg border border-solid border-gray-400 bg-gradient-to-tr from-cyan-100 to-green-100 px-14 py-14 text-center">
          <CameraIcon className="w-12 text-gray-400" />
          <p>No Image</p>
          {locked ? null : (
            <Tooltip content="Add an Image">
              <button
                className="btn btn-primary absolute bottom-0 right-0"
                onClick={open}
                disabled={locked}
              >
                <PlusIcon className="w-6" />
              </button>
            </Tooltip>
          )}
        </div>
      )}
      <DialogComponent
        isOpen={showDialog}
        onClose={close}
        aria-label="Upload Recipe Image"
      >
        <GetImageURL
          onCompleted={(url) => {
            close();

            updateRecipe({
              where: { id: recipe.id },
              data: {
                image: { set: url },
              },
            });
            onUpdateRecipeCompleted();
          }}
        />
        <button className="btn" onClick={close}>
          Close
        </button>
      </DialogComponent>
    </div>
  );
};

interface HeadlessRecipeImageProps {
  recipe: NonNullable<RecipeQuery["recipe"]>;
  fetching: boolean;
  user: CurrentUser;
  updateRecipe: UseMutationExecute<
    UpdateRecipeMutation,
    Exact<{
      where: RecipeWhereUniqueInput;
      data: RecipeUpdateInput;
    }>
  >;
  onUpdateRecipeCompleted: () => void;
}

export const HeadlessRecipeImage: React.FC<HeadlessRecipeImageProps> = ({
  recipe,
  fetching,
  user,
  updateRecipe,
  onUpdateRecipeCompleted,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);

  const locked = isRecipeLocked(recipe, user);

  return locked ? null : (
    <div className="flex items-center gap-2">
      {recipe.image ? (
        <>
          <p className="text-xl">Image:</p>
          <button className="btn" onClick={open} disabled={locked}>
            <ArrowPathIcon className="w-5" /> Replace
          </button>
          <button
            className="btn btn-warning"
            disabled={locked}
            onClick={() => {
              updateRecipe({
                where: { id: recipe.id },
                data: {
                  image: { set: null },
                },
              });
              onUpdateRecipeCompleted();
            }}
          >
            <TrashIcon className="w-5" /> Remove
          </button>
        </>
      ) : (
        <>
          {/* <Camera className="w-12 text-gray-400" /> */}
          <p className="text-xl">No Image</p>
          <button className="btn btn-primary" onClick={open} disabled={locked}>
            <PlusIcon className="w-5" /> Add
          </button>
        </>
      )}
      <DialogComponent
        isOpen={showDialog}
        onClose={close}
        aria-label="work in progress"
      >
        <GetImageURL
          onCompleted={(url) => {
            close();

            updateRecipe({
              where: { id: recipe.id },
              data: {
                image: { set: url },
              },
            });
            onUpdateRecipeCompleted();
          }}
        />
        <button className="btn" onClick={close}>
          Close
        </button>
      </DialogComponent>
    </div>
  );
};

interface Props {
  user: CurrentUser;
  recipe: OrderCarouselQuery["recipes"][number];
  isFragment?: boolean;
  isNative?: boolean;
  selectedRecipes?: string[];
  setSelectedRecipes?: Dispatch<SetStateAction<string[]>>;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  folder:
    | NonNullable<CurrentUser["school"]>["recipeCollections"][0]
    | null
    | undefined;
  search?: string;
}

const RecipesGridItem: React.FC<Props> = ({
  user,
  recipe,
  isFragment,
  isNative,
  selectedRecipes,
  setSelectedRecipes,
  sortedFolders,
  folder,
  search,
}) => {
  const [archived, setArchived] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [expand, setExpand] = useState(!!isFragment);

  const [{ fetching, error }, updateRecipe] = useUpdateRecipeMutation();

  return archived ? null : !expand ? (
    <div
      id={`${recipe.id}${isFragment ? "" : "-default"}`}
      className={classNames(
        isFragment ? "border-4 border-green-400" : "",
        "group relative bg-white transition-all",
        selectedRecipes?.includes(recipe.id)
          ? "ring ring-blue-400 ring-offset-2"
          : ""
      )}
    >
      {selectedRecipes && setSelectedRecipes ? (
        <Tooltip content="Select recipe for bulk actions.">
          <input
            type="checkbox"
            id={`select-recipe-${recipe.id}`}
            name={`select-recipe-${recipe.id}`}
            checked={selectedRecipes.includes(recipe.id)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedRecipes([...selectedRecipes, recipe.id]);
              } else {
                setSelectedRecipes(
                  selectedRecipes.filter((id) => id !== recipe.id)
                );
              }
            }}
            className="absolute -left-1 -top-1 z-10 size-6 rounded-full border-gray-300 text-blue-600 opacity-0 transition focus:ring-blue-600 group-hover:opacity-100"
          />
        </Tooltip>
      ) : null}
      <Link
        prefetch={false}
        href={`/event-log?id=${recipe.id}`}
        className="absolute -right-3 -top-3 z-10 rounded-full bg-white p-0.5 opacity-0 shadow-md transition delay-150 group-hover:opacity-100 print:hidden"
      >
        <Tooltip content="See Recipe Version History" placement="right">
          <span>
            <YoutubeSearchedForIcon className="icon my-1.5" />
          </span>
        </Tooltip>
      </Link>

      <div
        className="group"
        style={{
          display: "flex",
          justifyContent: "start",
          alignItems: "start",
        }}
      >
        <div className="w-20 text-center">
          {recipe.image ? (
            <div
              className="h-20 w-20 rounded-full bg-cover bg-center shadow-lg"
              style={{ backgroundImage: `url(${recipe.image})` }}
            />
          ) : (
            <>
              <div className="flex h-20 w-20 items-center justify-center rounded-full border border-solid border-cyan-300 bg-gradient-to-tr from-cyan-100 to-green-100 opacity-50 shadow-lg transition-all group-hover:opacity-100" />
            </>
          )}
        </div>
        <div
          style={{ margin: "5px" }}
          className="flex flex-1 items-start justify-between"
        >
          <div>
            <div className="mb-5 ml-2">
              <h3 className="mb-3 font-medium">{recipe.name}</h3>
              <small className="mb-2 block font-normal">
                Created:{" "}
                {format(new Date(recipe.createdAt), "p EEEE LLLL do, yyyy")}
              </small>
              <small className="mb-2 block">
                Updated:{" "}
                {format(new Date(recipe.updatedAt), "p EEEE LLLL do, yyyy")}
              </small>
              <GetLatestOrderSessions recipeId={recipe.id}>
                {(orderSessions) =>
                  orderSessions.length ? (
                    <>
                      {orderSessions.slice(0, 1).map((orderSession) => (
                        <Tooltip
                          content={format(
                            new Date(orderSession.createdAt),
                            "p EEEE LLLL do, yyyy"
                          )}
                          key={orderSession.id}
                        >
                          <small className="inline-block rounded border border-solid border-cyan-200 bg-cyan-50 px-1.5 py-0.5">
                            Last ordered{" "}
                            {formatDistanceToNow(
                              new Date(orderSession.createdAt),
                              { addSuffix: true }
                            )}
                          </small>
                        </Tooltip>
                      ))}
                    </>
                  ) : null
                }
              </GetLatestOrderSessions>
              {search ? (
                <div className="flex items-center gap-2 rounded border border-blue-400 border-opacity-50 bg-blue-50 p-1 text-sm">
                  <FolderIcon className="w-5 text-blue-400" />{" "}
                  {getPathToFolder(
                    sortedFolders.find(
                      (f) => f.id == recipe.recipeCollection?.id
                    ),
                    sortedFolders
                  )
                    .map(
                      (id) =>
                        sortedFolders.find((c) => c.id === id)?.name ||
                        "unknown folder"
                    )
                    .join(" / ")}
                </div>
              ) : null}
            </div>
            {recipe.tags?.length || recipe.classes?.length ? (
              <div>
                {recipe.classes.map((t) => (
                  <span key={t.id} className="tag tag-cyan">
                    {t.name}
                  </span>
                ))}
                {recipe.tags.map((t) => (
                  <span key={t.id} className="tag">
                    {t.name}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <UserContext.Consumer>
            {(user) => (
              //
              <div className="w-min">
                <a
                  href={`https://recipe.classcart.com/?id=${recipe.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Tooltip content="View Printable Page">
                    <button className="">
                      <PrinterIcon className="m-1.5 mt-0 w-6 text-cyan-700 hover:text-cyan-900" />
                    </button>
                  </Tooltip>
                </a>

                {user.school &&
                !(isStudent(user) && !user.school.permMakeOrders) ? (
                  <OrderDialogContext.Consumer>
                    {({ setOrderDialog, setRecipeToOrder }) => (
                      <Tooltip content="Order This Recipe">
                        <button
                          className="z-10"
                          onClick={() => {
                            setRecipeToOrder(recipe.id);
                            setOrderDialog(true);
                          }}
                        >
                          <ShoppingCartIcon className="m-1.5 mb-0 w-6 text-green-600 hover:text-green-700" />
                        </button>
                      </Tooltip>
                    )}
                  </OrderDialogContext.Consumer>
                ) : null}

                {isNative ? (
                  <DeleteRecipe
                    recipe={recipe}
                    user={user}
                    setDeleted={setDeleted}
                    setSelectedRecipes={setSelectedRecipes}
                  />
                ) : null}

                {recipe.locked ? (
                  isTeacher(user) ? (
                    <Tooltip
                      content={
                        <div className="font-normal">
                          <h2 className="mb-2 text-white">
                            This Recipe Is Locked
                          </h2>
                          {recipe.lockedBy ? (
                            <p>
                              Press this button to unlock it. This recipe was
                              locked by{" "}
                              <span className="font-medium text-cyan-600">
                                {recipe.lockedBy.name}
                              </span>
                              . An email will be sent to them when it is
                              unlocked.
                            </p>
                          ) : (
                            <p>Press this button to unlock it for students.</p>
                          )}
                        </div>
                      }
                    >
                      <button
                        className=""
                        disabled={fetching}
                        onClick={async () => {
                          if (recipe.lockedBy) {
                            await updateRecipe({
                              where: { id: recipe.id },
                              data: {
                                locked: { set: false },
                                lockedBy: {
                                  disconnect: {
                                    id: { equals: recipe.lockedBy?.id },
                                  },
                                },
                              },
                            });
                          } else {
                            await updateRecipe({
                              where: { id: recipe.id },
                              data: {
                                locked: { set: false },
                              },
                            });
                          }
                        }}
                      >
                        {fetching ? (
                          <div className="spinner h-6 w-6" />
                        ) : (
                          <LockClosedIcon className="m-1.5 mt-0 w-6 text-gray-800 hover:text-gray-900" />
                        )}
                      </button>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      content={
                        <div className="font-normal">
                          <h2 className="mb-2 text-white">
                            This Recipe Is Locked
                          </h2>
                          <p>
                            Locked recipes are read only and cannot be edited by
                            students.
                          </p>
                        </div>
                      }
                    >
                      <LockClosedIcon className="m-1.5 mt-0 w-6 text-gray-800 hover:text-gray-900" />
                    </Tooltip>
                  )
                ) : isTeacher(user) ? (
                  <Tooltip
                    content={
                      <div className="font-normal">
                        <h2 className="mb-2 text-white">Lock This Recipe</h2>
                        <p>
                          Locking a recipe makes it read only for students and
                          prevents them making changes. They can still duplicate
                          the recipe.
                        </p>
                      </div>
                    }
                  >
                    <button
                      disabled={fetching}
                      onClick={async () => {
                        await updateRecipe({
                          where: { id: recipe.id },
                          data: {
                            locked: { set: true },
                          },
                        });
                      }}
                    >
                      {fetching ? (
                        <div className="spinner h-6 w-6" />
                      ) : (
                        <LockOpenIcon className="m-1.5 mt-0 w-6 text-gray-800 hover:text-gray-900" />
                      )}
                    </button>
                  </Tooltip>
                ) : null}
              </div>
            )}
          </UserContext.Consumer>
        </div>
      </div>
      <div
        className="group/chevron absolute bottom-0 left-0 z-0 flex h-0 w-full cursor-pointer items-center justify-center"
        style={{ margin: 0 }}
        title="Expand"
        onClick={() => setExpand(true)}
      >
        <button className="w-full">
          <ChevronDownIcon className="m-1.5 mb-10 w-5 transform transition group-hover/chevron:scale-150" />
        </button>
      </div>
    </div>
  ) : (
    <GetRecipe id={recipe.id} spinner={true}>
      {(recipe) =>
        recipe ? (
          <ExpandedItem
            {...{
              user,
              recipe,
              isFragment,
              setExpand,
              archived,
              setDeleted,
              setArchived,
              selectedRecipes,
              setSelectedRecipes,
              sortedFolders,
              folder,
            }}
          />
        ) : (
          <p>Recipe Not Found!</p>
        )
      }
    </GetRecipe>
  );
};

export default RecipesGridItem;
